import { useState, useCallback, useEffect, useRef } from "react";
import EnhancedCheckIn from "@/components/EnhancedCheckIn";
import { useToast } from "@/components/ui/use-toast";
import { CheckInData, ActionSuggestion, ReflectionAnalysis } from "@/types/user";
import { insightsService } from "@/services/insightsService";
import {
  analyzeReflection,
  insertCheckInData,
} from "@/components/chat/services/checkInService";
import ActionSuggestions from "@/components/ActionSuggestions";
import { useUser } from "@/contexts/UserContext";
import { CheckSquare, Edit, Calendar, TrendingUp, Target } from "lucide-react";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getUserStats, UserStats } from "@/services/userStatsService";
import { useRefresh } from "@/contexts/RefreshContext";
import { wellnessService } from "@/services/wellnessService";

const CheckInPage = () => {
  const { user: authUser } = useAuth();
  const { toast } = useToast();
  const { user, loading, isAuthenticated } = useUser();
  const navigate = useNavigate();
  const { triggerCheckInRefresh } = useRefresh();
  const [actionSuggestions, setActionSuggestions] = useState<
    ActionSuggestion[]
  >([]);
  const [aiInsights, setAiInsights] = useState<ReflectionAnalysis | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  useEffect(() => {
    setSubmitting(false);
  }, []);
  const [checkInSubmitted, setCheckInSubmitted] = useState(false);
  const [hasCheckedInToday, setHasCheckedInToday] = useState(false);
  const [existingCheckIn, setExistingCheckIn] = useState<CheckInData | null>(
    null
  );
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [selectedCheckInDate, setSelectedCheckInDate] = useState<Date>(new Date());

  // Use ref to prevent navigation dependency loops
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      toast({
        title: "Authentication required",
        description: "Please sign in to access this page.",
        variant: "destructive",
      });
      navigateRef.current("/auth");
    }
  }, [loading, isAuthenticated, toast]); // FIXED: Removed navigate from dependencies

  useEffect(() => {
    const checkTodayCheckIn = async () => {
      if (!authUser?.id) {
        return;
      }

      try {
        // wellnessService.getTodaysCheckIn() returns data directly, not { success, data }
        const todayCheckIn = await wellnessService.getTodaysCheckIn();

        if (todayCheckIn) {
          setHasCheckedInToday(true);
          setExistingCheckIn({
            id: todayCheckIn.id,
            userId: todayCheckIn.userId,
            date: todayCheckIn.date,
            energy: todayCheckIn.energy,
            focus: todayCheckIn.focus,
            mood: todayCheckIn.mood,
            motivation: todayCheckIn.motivation || 50,
            reflection: todayCheckIn.reflection || "",
            wins: todayCheckIn.wins || "",
            blockers: todayCheckIn.blockers || "",
            tasks: todayCheckIn.tasks || [],
            helpNeeded: todayCheckIn.helpNeeded || false,
            helpWith: todayCheckIn.helpWith || "",
          });
        } else {
          setHasCheckedInToday(false);
          setExistingCheckIn(null);
        }
      } catch (error) {
        console.error("Error checking today's check-in:", error);
        setHasCheckedInToday(false);
        setExistingCheckIn(null);
      }
    };

    if (authUser?.id) {
      checkTodayCheckIn();
    }
  }, [authUser?.id]); // FIXED: Removed selectedCheckInDate to prevent infinite loop

  useEffect(() => {
    const loadUserStats = async () => {
      if (!authUser?.id) {
        setLoadingStats(false);
        return;
      }

      try {
        setLoadingStats(true);
        const stats = await getUserStats();
        setUserStats(stats);
      } catch (error) {
        console.error('Error loading user stats:', error);
        // ALIGNED WITH web_old: Fallback stats matching web_old format
        setUserStats({
          currentStreak: 0,
          longestStreak: 0,
          checkInsThisWeek: 0,
          checkInsThisMonth: 0,
          totalCheckIns: 0,
          lastCheckInDate: null,
        });
      } finally {
        setLoadingStats(false);
      }
    };

    if (authUser?.id) {
      loadUserStats();
    }
  }, [authUser?.id, checkInSubmitted]);

  const loadCheckInForDate = useCallback(async (date: Date) => {
    if (!authUser?.id) return;

    try {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      // wellnessService.getCheckIns() returns an array directly
      const checkIns = await wellnessService.getCheckIns({
        startDate: dateString,
        endDate: dateString,
        limit: 1
      });

      const checkInsArray = Array.isArray(checkIns) ? checkIns : [];
      
      // Check if this is today's date
      const now = new Date();
      const todayYear = now.getFullYear();
      const todayMonth = String(now.getMonth() + 1).padStart(2, '0');
      const todayDay = String(now.getDate()).padStart(2, '0');
      const today = `${todayYear}-${todayMonth}-${todayDay}`;
      const isToday = dateString === today;
      
      if (checkInsArray.length > 0) {
        const checkIn = checkInsArray[0];
        setExistingCheckIn({
          id: checkIn.id,
          userId: checkIn.userId,
          date: checkIn.date,
          energy: checkIn.energy,
          focus: checkIn.focus,
          mood: checkIn.mood,
          motivation: checkIn.motivation || 50,
          reflection: checkIn.reflection || "",
          wins: checkIn.wins || "",
          blockers: checkIn.blockers || "",
          tasks: checkIn.tasks || [],
          helpNeeded: checkIn.helpNeeded || false,
          helpWith: checkIn.helpWith || "",
        });
        if (isToday) {
          setHasCheckedInToday(true);
        }
      } else {
        setExistingCheckIn(null);
        if (isToday) {
          setHasCheckedInToday(false);
        }
      }
    } catch (error) {
      console.error("Error loading check-in for date:", error);
      setExistingCheckIn(null);
      // Don't update hasCheckedInToday on error to avoid flickering
    }
  }, [authUser?.id]);

  const handleDateChange = useCallback((date: Date) => {
    setSelectedCheckInDate(date);
    // loadCheckInForDate will be called automatically by useEffect when selectedCheckInDate changes
    // No need to call it here to avoid double calls
  }, []);

  useEffect(() => {
    if (authUser?.id) {
      loadCheckInForDate(selectedCheckInDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id, selectedCheckInDate]);

  const handleCheckInSubmit = async (
    checkInData: CheckInData,
    insights: ReflectionAnalysis | null
  ) => {
    console.log('[CheckIn] handleCheckInSubmit called', { checkInData });
    
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to submit a check-in.",
        variant: "destructive",
      });
      return;
    }

    const userId = authUser?.id;
    if (!userId) {
      toast({
        title: "Authentication required",
        description: "Please sign in to submit a check-in.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const enhancedCheckInData: CheckInData = {
        ...checkInData,
        userId: userId,
      };

      console.log('[CheckIn] Calling insertCheckInData...');
      await insertCheckInData(userId, enhancedCheckInData);
      console.log('[CheckIn] insertCheckInData completed successfully');

      // ALIGNED WITH web_old: Always analyze reflection if present (including from dynamic questions)
      // MIGRATED FROM web_old/src/pages/CheckIn.tsx::handleCheckInSubmit() (lines 265-286)
      let analysisResults = insights;
      if (checkInData.reflection) {
        console.log('🔍 Analyzing reflection:', checkInData.reflection);
        try {
          // ALIGNED WITH web_old: Use insights service for real analysis (lines 271-274)
          // Backend endpoint: POST /insights/analyze
          // Backend will fetch history context automatically (better than web_old)
          const analysis = await insightsService.analyzeReflection({
            reflection: checkInData.reflection,
            moodScore: checkInData.mood,
            energyLevel: checkInData.energy,
            focusLevel: checkInData.focus,
          });
          console.log('✨ AI analysis completed:', analysis);
          analysisResults = analysis;
          setAiInsights(analysis);
        } catch (error) {
          console.error("Error analyzing reflection:", error);
          // ALIGNED WITH web_old: Use fallback analysis (lines 279-282)
          analysisResults = await analyzeReflection(checkInData.reflection);
          setAiInsights(analysisResults);
        }
      } else {
        console.log('⚠️ No reflection text to analyze');
      }

      if (checkInData.helpNeeded && checkInData.helpWith) {
        toast({
          title: "Help request recorded",
          description:
            "Your team has been notified about your request for assistance.",
          variant: "default",
        });
      }

      // ALIGNED WITH web_old: Generate action suggestions if analysis results exist (lines 297-333)
      // MIGRATED FROM web_old/src/pages/CheckIn.tsx::handleCheckInSubmit()
      if (analysisResults) {
        try {
          // ALIGNED WITH web_old: Get recent check-ins for better AI context (lines 299-319)
          // Backend endpoint: POST /insights/suggestions
          // Backend will fetch recent history automatically (better than web_old which fetches in frontend)
          const historyData = await wellnessService.getCheckIns({ limit: 7 });
          
          // Transform database format to CheckInData format
          const recentCheckIns: CheckInData[] = (Array.isArray(historyData) ? historyData : []).map((item: any) => ({
            id: item.id,
            userId: item.userId,
            date: item.date,
            mood: item.mood,
            energy: item.energy,
            focus: item.focus || 50,
            motivation: item.motivation || 50,
            tasks: item.tasks || [],
            wins: item.wins || "",
            blockers: item.blockers || "",
            reflection: item.reflection || "",
            helpNeeded: item.helpNeeded || false,
            helpWith: item.helpWith || "",
            dynamicQuestions: item.dynamicQuestions || undefined,
          }));
          
          console.log('🎯 Generating AI suggestions for check-in...', {
            checkInData: enhancedCheckInData,
            persona: user?.persona,
            historyLength: recentCheckIns.length
          });
          
          // ALIGNED WITH web_old: Call backend endpoint (lines 327-331)
          // Backend will use recentCheckIns internally, but we pass for logging/context
          const suggestionsResponse = await insightsService.generateSuggestions({
            currentCheckIn: enhancedCheckInData,
            limit: 5,
          });
          const suggestions = Array.isArray(suggestionsResponse) ? suggestionsResponse : (suggestionsResponse?.suggestions || []);
          
          console.log('✅ AI suggestions generated:', suggestions);
          setActionSuggestions(suggestions);
        } catch (error) {
          console.error("Error generating suggestions:", error);
          console.error("Error details:", error.message || error);
          // Fallback to role-specific suggestions if AI fails
          const isManager = user?.persona === 'manager' || user?.role === 'manager';
          const fallbackSuggestions = isManager ? [
            {
              id: 'fallback-manager-1',
              title: 'Check in with your team',
              description: 'Spend 10 minutes connecting with team members and offering support',
              actionType: 'connect' as const,
              priority: 'high' as const,
              duration: 10
            },
            {
              id: 'fallback-manager-2',
              title: 'Review team priorities',
              description: 'Look at upcoming deadlines and ensure resources are allocated properly',
              actionType: 'reflect' as const,
              priority: 'high' as const,
              duration: 15
            },
            {
              id: 'fallback-manager-3',
              title: 'Plan your 1-on-1s',
              description: 'Schedule or prepare talking points for upcoming team meetings',
              actionType: 'focus' as const,
              priority: 'medium' as const,
              duration: 20
            }
          ] : [
            {
              id: 'fallback-1',
              title: 'Take a 5-minute break',
              description: 'Step away from your work and take a few deep breaths',
              actionType: 'break' as const,
              priority: 'medium' as const,
              duration: 5
            },
            {
              id: 'fallback-2', 
              title: 'Review your priorities',
              description: 'Look at your task list and identify the most important items',
              actionType: 'reflect' as const,
              priority: 'high' as const,
              duration: 10
            },
            {
              id: 'fallback-3',
              title: 'Connect with a colleague',
              description: 'Reach out to someone on your team for a quick chat',
              actionType: 'connect' as const,
              priority: 'low' as const,
              duration: 15
            }
          ];
          setActionSuggestions(fallbackSuggestions);
        }
      }

      console.log('[CheckIn] Setting checkInSubmitted to true');
      setCheckInSubmitted(true);
      setHasCheckedInToday(true); // Set immediately after successful submission
      
      // Trigger refresh for AI insights after successful check-in
      triggerCheckInRefresh();
      console.log('[CheckIn] Check-in submission complete, showing success screen');
      
      // Double-check and reload stats to confirm persistence
      if (authUser?.id) {
        try {
          const updatedStats = await getUserStats();
          setUserStats(updatedStats);
          
          // Verify today's check-in exists in database
          const todayCheckIn = await wellnessService.getTodaysCheckIn();
          if (todayCheckIn) {
            console.log('[CheckIn] Verified check-in saved:', todayCheckIn.id);
            setHasCheckedInToday(true);
            setExistingCheckIn({
              id: todayCheckIn.id,
              userId: todayCheckIn.userId,
              date: todayCheckIn.date,
              energy: todayCheckIn.energy,
              focus: todayCheckIn.focus,
              mood: todayCheckIn.mood,
              motivation: todayCheckIn.motivation || 50,
              reflection: todayCheckIn.reflection || "",
              wins: todayCheckIn.wins || "",
              blockers: todayCheckIn.blockers || "",
              tasks: todayCheckIn.tasks || [],
              helpNeeded: todayCheckIn.helpNeeded || false,
              helpWith: todayCheckIn.helpWith || "",
            });
          } else {
            console.warn('[CheckIn] Could not verify check-in after submission');
          }
        } catch (error) {
          console.error('Error reloading data after check-in:', error);
          // Don't reset hasCheckedInToday - we already submitted successfully
        }
      }
    } catch (error) {
      console.error("[CheckIn] Error submitting check-in:", error);
      toast({
        title: "Submission error",
        description: `There was a problem submitting your check-in: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        variant: "destructive",
      });
    } finally {
      console.log('[CheckIn] Submission process finished, submitting=false');
      setSubmitting(false);
    }
  };

  const handleActionSelected = useCallback(
    (action: ActionSuggestion) => {
      console.log('[CheckIn] handleActionSelected called for action:', action.id, action.title);
      
      // Only remove the selected action from the list
      // This ensures that only the clicked action disappears, others remain
      setActionSuggestions((prev) => {
        console.log('[CheckIn] Before filter - total suggestions:', prev.length);
        const filtered = prev.filter((item) => item.id !== action.id);
        console.log('[CheckIn] After filter - remaining suggestions:', filtered.length, 'Removed:', action.id);
        console.log('[CheckIn] Remaining suggestion IDs:', filtered.map(s => s.id));
        return filtered;
      });
    },
    []
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <div className="text-center space-y-4">
          <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
          <p className="text-muted-foreground">Loading check-in form...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect from useEffect
  }

  return (
    <div className="max-w-2xl mx-auto py-8">
        {!checkInSubmitted ? (
          <>
            {/* Enhanced header with progress tracking */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="p-2 bg-primary/10 rounded-full">
                  <Calendar className="h-6 w-6 text-primary" />
                </div>
                <h1 className="text-3xl font-bold tracking-tight">
                  My Daily Check-in
                </h1>
              </div>
              
              {hasCheckedInToday ? (
                <div className="flex items-center justify-center gap-2 text-amber-600 bg-amber-50 px-4 py-2 rounded-full border border-amber-200">
                  <Edit className="h-4 w-4" />
                  <p className="font-medium">Editing today's check-in</p>
                </div>
              ) : (
                <p className="text-muted-foreground text-lg">
                  Track your personal wellbeing and reflect on your day
                </p>
              )}
            </div>

            {/* Progress stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                <CardContent className="p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <TrendingUp className="h-4 w-4 text-green-600" />
                    <span className="font-semibold text-green-700">This Week</span>
                  </div>
                  <div className="text-2xl font-bold text-green-800">
                    {loadingStats ? (
                      <div className="animate-pulse bg-green-200 h-8 w-12 rounded mx-auto"></div>
                    ) : (
                      `${userStats?.checkInsThisWeek || 0}/7`
                    )}
                  </div>
                  <p className="text-xs text-green-600">Check-ins completed</p>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-blue-50 to-sky-50 border-blue-200">
                <CardContent className="p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <CheckSquare className="h-4 w-4 text-blue-600" />
                    <span className="font-semibold text-blue-700">Streak</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-800">
                    {loadingStats ? (
                      <div className="animate-pulse bg-blue-200 h-8 w-16 rounded mx-auto"></div>
                    ) : (
                      userStats?.currentStreak === 0 ? "0 days" :
                      userStats?.currentStreak === 1 ? "1 day" : 
                      `${userStats?.currentStreak || 0} days`
                    )}
                  </div>
                  <p className="text-xs text-blue-600">Current streak</p>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
                <CardContent className="p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Target className="h-4 w-4 text-purple-600" />
                    <span className="font-semibold text-purple-700">Goal</span>
                  </div>
                  <div className="text-2xl font-bold text-purple-800">
                    {loadingStats ? (
                      <div className="animate-pulse bg-purple-200 h-8 w-12 rounded mx-auto"></div>
                    ) : (
                      `${userStats?.checkInsThisWeek || 0}/7`
                    )}
                  </div>
                  <p className="text-xs text-purple-600">Weekly target</p>
                </CardContent>
              </Card>
            </div>

            {/* Check-in Form */}
            <EnhancedCheckIn
              onSubmit={handleCheckInSubmit}
              isSubmitting={submitting}
              existingCheckIn={existingCheckIn}
              onDateChange={handleDateChange}
              selectedCheckInDate={selectedCheckInDate}
            />
          </>
        ) : (
          <div className="space-y-8 animate-in fade-in-50">
            <div className="text-center">
              <div className="inline-flex items-center justify-center p-2 bg-green-100 rounded-full mb-4">
                <div className="h-12 w-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckSquare className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <h1 className="text-3xl font-bold tracking-tight mb-2">
                Check-in Complete
              </h1>
              <p className="text-muted-foreground">
                Thanks for sharing your update!
              </p>
            </div>

            {actionSuggestions.length > 0 && (
              <div className="mt-8">
                <ActionSuggestions
                  onActionSelected={handleActionSelected}
                  preGeneratedSuggestions={actionSuggestions}
                />
              </div>
            )}

          </div>
        )}
      </div>
  );
};

export default CheckInPage;
